import dayjs from "@calcom/dayjs";
import { useCalendarStore } from "@calcom/features/calendars/weeklyview/state/store";
import type { BorderColor } from "@calcom/features/calendars/weeklyview/types/common";
import type { CalendarEvent } from "@calcom/features/calendars/weeklyview/types/events";
import classNames from "@calcom/ui/classNames";

type Props = {
  days: dayjs.Dayjs[];
  showBorder: boolean;
  borderColor: BorderColor;
};

/** Height of one chip + its gap. Three chips fit before the row starts scrolling. */
const CHIP_HEIGHT = 22;
const MAX_VISIBLE_ROWS = 3;

/**
 * The all-day band, drawn between the day headers and the time grid — the row a Google/Notion
 * calendar puts holidays, birthdays and multi-day blocks in. Renders nothing at all when the
 * consumer passes no `allDayEvents`, so every existing consumer of `Calendar` is unaffected.
 *
 * Unlike the time grid, an event here can span columns: a 3-day event is one chip stretched across
 * the days it covers that are visible in the current range.
 */
export function AllDayRow({ days, showBorder, borderColor }: Props) {
  const allDayEvents = useCalendarStore((state) => state.allDayEvents);
  const onEventClick = useCalendarStore((state) => state.onEventClick);

  if (!allDayEvents?.length || !days.length) return null;

  const rangeStart = days[0].startOf("day");
  const rangeEnd = days[days.length - 1].endOf("day");

  const spans = allDayEvents
    .map((event) => {
      const start = dayjs(event.start);
      // An all-day event's end is exclusive in most providers, but callers hand us the inclusive
      // instant they want painted, so clamp to at least the start day.
      const end = dayjs(event.end);
      if (end.isBefore(rangeStart) || start.isAfter(rangeEnd)) return null;

      const firstIdx = Math.max(
        0,
        days.findIndex((day) => day.isSame(start, "day"))
      );
      const lastIdxRaw = days.findIndex((day) => day.isSame(end, "day"));
      const lastIdx = lastIdxRaw === -1 ? days.length - 1 : lastIdxRaw;

      return { event, firstIdx, span: Math.max(1, lastIdx - firstIdx + 1) };
    })
    .filter((span): span is { event: CalendarEvent; firstIdx: number; span: number } => span !== null);

  if (!spans.length) return null;

  return (
    <div
      className={classNames(
        "bg-default dark:bg-cal-muted flex flex-none border-b",
        borderColor === "subtle" ? "border-b-subtle" : "border-b-default",
        showBorder && (borderColor === "subtle" ? "border-r-subtle border-r" : "border-r-default border-r")
      )}>
      <div
        className={classNames(
          "text-muted flex w-16 flex-none items-start justify-center pt-1 text-[10px] uppercase",
          showBorder &&
            (borderColor === "subtle"
              ? "border-l-subtle border-r-subtle border-l border-r"
              : "border-l-default border-r-default border-l border-r")
        )}>
        Todo el día
      </div>
      <div
        className="grid flex-auto gap-px overflow-y-auto py-1"
        style={{
          gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))`,
          maxHeight: CHIP_HEIGHT * MAX_VISIBLE_ROWS + 8,
        }}>
        {spans.map(({ event, firstIdx, span }) => {
          const interactive = Boolean(onEventClick);
          const Tag = interactive ? "button" : "div";
          return (
            <Tag
              key={`${event.id}-${firstIdx}`}
              data-calendar-event-id={String(event.id)}
              type={interactive ? "button" : undefined}
              onClick={interactive ? () => onEventClick?.(event) : undefined}
              title={event.title}
              className={classNames(
                "bg-subtle text-emphasis mx-1 flex items-center gap-1 truncate rounded-[6px] px-2 text-xs font-medium leading-5",
                interactive && "hover:bg-emphasis cursor-pointer",
                event.options?.className
              )}
              style={{ gridColumn: `${firstIdx + 1} / span ${span}`, height: CHIP_HEIGHT - 4 }}>
              {event.options?.color && (
                <span
                  className="h-2 w-2 flex-none rounded-full"
                  style={{ backgroundColor: event.options.color }}
                />
              )}
              <span className="truncate">{event.title}</span>
            </Tag>
          );
        })}
      </div>
    </div>
  );
}
